/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-explicit-any */
import ScoreBadge from "@/components/score-badge";
import { mapToColorClasses } from "@/lib/colors";

import { cn, dateFormatter } from "@/lib/utils";
import { ColDef } from "ag-grid-community";
import { Inbox, MailWarning, OctagonAlert } from "lucide-react";
import { authProviderIcons } from "../mailbox/esp-providers";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BreakdownData {
  mainStatus: string;
  breakdown?: Record<string, number>;
  totalCount: Record<string, number>;
}

// Helper functions for the PlacementCell
const getStatusColor = (status: string) => {
  if (status === "Inbox" || status === "Clean") return "text-green-600";
  if (status === "Spam" || status === "Blocked" || status === "Listed")
    return "text-red-600";
  if (status === "Undelivered") return "text-amber-600";
  return "text-gray-600";
};

const getStatusIcon = (status: string) => {
  if (status === "Inbox" || status === "Clean") return <Inbox size={16} />;
  if (status === "Spam" || status === "Blocked" || status === "Listed")
    return <OctagonAlert size={16} />;
  if (status === "Undelivered") return <MailWarning size={16} />;
  return null;
};

const renderBasicStatus = (status: string) => {
  return (
    <div className={`flex items-center gap-1 ${getStatusColor(status)}`}>
      {getStatusIcon(status)}
      <span>{status}</span>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const PlacementCell = (props: { value: any }) => {
  const data = props.value;

  // If no data, show empty cell
  if (!data) {
    return <div className="h-full flex items-center">-</div>;
  }

  // For the new API structure with status and deliveryRate
  if (data.status) {
    const statusText = data.status.toUpperCase();
    const deliveryRate = data.deliveryRate;
    const category = data.category ? ` (${data.category})` : "";

    return (
      <div className="h-full flex items-center">
        <div className="flex items-center text-xs font-medium">
          <span
            className={cn(
              "px-2 py-1 rounded-l-md",
              statusText === "PARTIAL-INBOX"
                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
                : mapToColorClasses(data.status.toLowerCase(), ["bg", "text"])
            )}
          >
            {statusText}
          </span>
          <span
            className={cn(
              "px-2 py-1 rounded-r-md bg-slate-100 text-slate-500 dark:bg-slate-900/20 dark:text-slate-400"
            )}
          >
            {deliveryRate}%{category}
          </span>
        </div>
      </div>
    );
  }

  // For backward compatibility with old format
  if (typeof data === "string") {
    return renderBasicStatus(data);
  }

  // For detailed breakdown with bar chart (old format)
  const totalEmails = Object.values(data.totalCount || {}).reduce<number>(
    (sum, count) => sum + (count as number),
    0
  );

  // Only proceed if we have data
  if (!totalEmails) return <div className="h-full flex items-center">-</div>;

  // in data.totalCount find the key that has the highest value
  const highestValueKey = Object.keys(data.totalCount).reduce((a, b) =>
    data.totalCount[a] > data.totalCount[b] ? a : b
  );

  // Get the count for the highest value key, ensuring it's a number
  const highestCount = Number(data.totalCount[highestValueKey] || 0);
  // Calculate percentage, defaulting to 0 if invalid
  const percentage =
    Number(totalEmails) > 0 ? (highestCount / Number(totalEmails)) * 100 : 0;

  return (
    <div className="h-full flex items-center">
      <div className="flex items-center text-xs font-medium uppercase">
        <span
          className={cn(
            "px-2 py-1 rounded-l-md",
            mapToColorClasses(highestValueKey.toLowerCase(), ["bg", "text"])
          )}
        >
          {highestValueKey}
        </span>

        <span
          className={cn(
            "px-2 py-1 rounded-r-md bg-slate-100 text-slate-500 dark:bg-slate-900/20 dark:text-slate-400"
          )}
        >
          {percentage}%
        </span>
      </div>
    </div>
  );
};

// Add custom cell renderers for all providers to handle potential casing/format variations
const Microsoft365Cell = (params: any) => {
  const value = params.data.microsoft365 || params.data["microsoft 365"];
  return <PlacementCell value={value} />;
};

const GoogleWorkspaceCell = (params: any) => {
  // Check both possible field names
  const value =
    params.data.googleworkspace ||
    params.data.googleWorkspace ||
    params.data["google workspace"];
  return <PlacementCell value={value} />;
};

const ZohoCell = (params: any) => {
  const value = params.data.zoho || params.data["zoho mail"];
  return <PlacementCell value={value} />;
};

const AmazonWorkMailCell = (params: any) => {
  const value = params.data.amazonWorkMail || params.data["workmail"];
  return <PlacementCell value={value} />;
};

const RspamdCell = (params: any) => {
  const value = params.data.rspamd || params.data["rspamd"];
  return <PlacementCell value={value} />;
};

const GmailCell = (params: any) => {
  const value = params.data.gmail;
  return <PlacementCell value={value} />;
};

const OutlookCell = (params: any) => {
  const value = params.data.outlook || params.data["outlook.com"];
  return <PlacementCell value={value} />;
};

const YahooCell = (params: any) => {
  const value = params.data.yahoo || params.data["yahoo mail"];
  return <PlacementCell value={value} />;
};

const AolCell = (params: any) => {
  const value = params.data.aol || params.data["aol mail"];
  return <PlacementCell value={value} />;
};

const IcloudCell = (params: any) => {
  const value = params.data.icloud || params.data["icloud mail"];
  return <PlacementCell value={value} />;
};

const GmxCell = (params: any) => {
  const value = params.data.gmx;
  return <PlacementCell value={value} />;
};

const AmazonAbuseExCell = (params: any) => {
  // Check both possible field names
  const value = params.data.amazonAbuseEx || params.data["amazon abusix"];
  return <PlacementCell value={value} />;
};

const AmazonTrendMicroCell = (params: any) => {
  // Check both possible field names
  const value =
    params.data.amazonTrendMicro || params.data["amazon trendmicro"];
  return <PlacementCell value={value} />;
};

const AmazonSpamhausCell = (params: any) => {
  // Check both possible field names
  const value = params.data.amazonSpamhaus || params.data["amazon spamhaus"];
  return <PlacementCell value={value} />;
};

// Provider mapping for icons and display names
const providerMapping: Record<
  string,
  { icon: string; displayName: string }
> = {
  microsoft365: {
    icon: "/providers-logos/microsoft-365.png",
    displayName: "Microsoft 365",
  },
  googleWorkspace: {
    icon: "/providers-logos/google-workspace.png",
    displayName: "Google Workspace",
  },
  zoho: {
    icon: "/providers-logos/zoho-mail.png",
    displayName: "Zoho WorkMail",
  },
  amazonWorkMail: {
    icon: "/providers-logos/aws-workmail.png",
    displayName: "Amazon WorkMail",
  },
  workmail: {
    icon: "/providers-logos/aws-workmail.png",
    displayName: "Amazon WorkMail",
  },
  "amazon abusix": {
    icon: "/providers-logos/abusix.png",
    displayName: "Abusix",
  },
  amazonAbuseEx: {
    icon: "/providers-logos/abusix.png",
    displayName: "Abusix",
  },
  amazonSpamhaus: {
    icon: "/providers-logos/spamhaus.png",
    displayName: "Spamhaus",
  },
  amazonTrendMicro: {
    icon: "/providers-logos/trend-micro.png",
    displayName: "Trend Micro",
  },
  rspamd: {
    icon: "/providers-logos/rspamd.png",
    displayName: "Rspamd",
  },
  gmail: {
    icon: "/providers-logos/gmail.png",
    displayName: "Gmail",
  },
  outlook: {
    icon: "/providers-logos/outlook.png",
    displayName: "Outlook.com",
  },
  yahoo: {
    icon: "/providers-logos/yahoo.png",
    displayName: "Yahoo! Mail",
  },
  aol: {
    icon: "/providers-logos/aol.png",
    displayName: "AOL Mail",
  },
  icloud: {
    icon: "/providers-logos/icloud.png",
    displayName: "iCloud Mail",
  },
  gmx: {
    icon: "/providers-logos/gmx.png",
    displayName: "GMX",
  },
  protonmail: {
    icon: "/providers-logos/proton-mail.png",
    displayName: "ProtonMail",
  },
};

// Helper function to get provider status border and background color
const getProviderStatusColor = (status: string) => {
  const statusLower = status?.toLowerCase() || "";
  if (statusLower === "inbox") return "border-green-500 bg-green-50 dark:bg-green-900/20";
  if (statusLower === "spam") return "border-red-500 bg-red-50 dark:bg-red-900/20";
  if (statusLower === "undelivered" || statusLower === "unreceived")
    return "border-amber-500 bg-amber-50 dark:bg-amber-900/20";
  return "border-gray-500 bg-gray-50 dark:bg-gray-900/20";
};

// Cell renderer for tested providers
const TestedProvidersCell = (params: any) => {
  const data = params.data;
  if (!data) return <div className="h-full flex items-center">-</div>;

  // Get all providers that have data
  const providersWithData: Array<{
    key: string;
    data: any;
    icon: string;
    displayName: string;
  }> = [];

  // Check all possible provider field names
  const providerFields = [
    "microsoft365",
    "googleWorkspace",
    "zoho",
    "amazonWorkMail",
    "workmail",
    "amazon abusix",
    "amazonAbuseEx",
    "amazonSpamhaus",
    "amazonTrendMicro",
    "rspamd",
    "gmail",
    "outlook",
    "yahoo",
    "aol",
    "icloud",
    "gmx",
    "protonmail",
  ];

  providerFields.forEach((field) => {
    // Check various possible field name variations
    const providerData =
      data[field] ||
      data[field.toLowerCase()] ||
      data[field.replace(/\s+/g, "")] ||
      data[field.replace(/\s+/g, "-")] ||
      data[field.replace(/\s+/g, "_")];
    
    if (providerData) {
      // Find the mapping key (handle variations)
      const mappingKey =
        Object.keys(providerMapping).find(
          (key) =>
            key === field ||
            key.toLowerCase() === field.toLowerCase() ||
            key.replace(/\s+/g, "") === field.replace(/\s+/g, "") ||
            key.replace(/\s+/g, "-") === field.replace(/\s+/g, "-")
        ) || field;

      const mapping = providerMapping[mappingKey] || {
        icon: "/mailIcon.png",
        displayName: field,
      };

      providersWithData.push({
        key: field,
        data: providerData,
        icon: mapping.icon,
        displayName: mapping.displayName,
      });
    }
  });

  if (providersWithData.length === 0) {
    return <div className="h-full flex items-center">-</div>;
  }

  // Split providers into visible (first 4) and remaining
  const visibleProviders = providersWithData.slice(0, 4);
  const remainingProviders = providersWithData.slice(4);
  const remainingCount = remainingProviders.length;

  const iconContainerClass =
    "flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg transition-transform hover:scale-105 overflow-hidden";

  return (
    <div className="h-full flex items-center gap-2 flex-nowrap min-w-0">
      {visibleProviders.map((provider, index) => {
        const status = provider.data?.status || "unknown";
        const deliveryRate = provider.data?.deliveryRate || 0;
        const statusColor = getProviderStatusColor(status);

        return (
          <Tooltip key={`${provider.key}-${index}`}>
            <TooltipTrigger asChild>
              <div className={cn(iconContainerClass, statusColor)}>
                <img
                  src={provider.icon}
                  alt={provider.displayName}
                  className="w-5 h-5 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/mailIcon.png";
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                <div className="font-medium">{provider.displayName}</div>
              </div>
            </TooltipContent>
          </Tooltip>
        );
      })}

      {remainingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                iconContainerClass,
                "cursor-pointer bg-gray-200/90 dark:bg-gray-700/90 border-gray-300 dark:border-gray-600"
              )}
            >
              <span className="text-xs font-semibold tabular-nums text-gray-600 dark:text-gray-300">
                +{remainingCount}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="flex items-center gap-2 flex-wrap p-0.5">
              {remainingProviders.map((provider, index) => (
                <div
                  key={`remaining-${provider.key}-${index}`}
                  className={cn(iconContainerClass, getProviderStatusColor(provider.data?.status || "unknown"))}
                  title={provider.displayName}
                >
                  <img
                    src={provider.icon}
                    alt={provider.displayName}
                    className="w-5 h-5 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/mailIcon.png";
                    }}
                  />
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

// Base columns that appear in all views
const baseColumnDefs: ColDef[] = [
  {
    headerName: "Email",
    field: "email",
    minWidth: 260,
    cellRenderer: (params: any) => (
      <div className="font-medium h-full flex items-center hover:underline">
        {params.value}
      </div>
    ),
  },
  {
    headerName: "Tested Providers",
    field: "testedProviders",
    minWidth: 200,
    flex: 1,
    cellRenderer: TestedProvidersCell,
  },
  {
    headerName: "Score",
    field: "score",
    minWidth: 100,
    maxWidth: 120,
    cellRenderer: (params: any) => {
      const score = params.value;
      return (
        <div className="flex items-center justify-center gap-2 h-full">
          <ScoreBadge score={score} thresholds={{ green: 90, yellow: 70 }} />
        </div>
      );
    },
  },
  {
    headerName: "Created At",
    minWidth: 200,
    cellRenderer: (params: { data: any }) => (
      <div className="h-full flex items-center">
        {dateFormatter(params.data.createdAt || params.data.date)}
      </div>
    ),
  },
  {
    headerName: "ESP Type",
    field: "espType",
    minWidth: 120,
    cellRenderer: (params: any) => {
      return (
        <div className="flex items-center h-full">
          <Tooltip>
            <TooltipTrigger asChild>
              {
                authProviderIcons[
                  params.value as keyof typeof authProviderIcons
                ]?.icon
              }
            </TooltipTrigger>
            <TooltipContent>
              {authProviderIcons[params.value as keyof typeof authProviderIcons]
                ?.tooltip || "Private SMTP"}
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  {
    headerName:"Average time to deliver",
    field: "sentAt",
    minWidth: 200,
    cellRenderer: (params: any) => {
      const timetaken = new Date(params.data.updatedAt).getTime() - new Date(params.data.sentAt).getTime();
      const minutes = Math.floor((timetaken % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timetaken % (1000 * 60)) / 1000);
      const timeString = `${minutes}m ${seconds}s`;
      return (
        <div className="h-full flex items-center">
          {timeString}
        </div>
      );
    },
  },
];

// B2C specific columns
export const b2cColumns = [
  // {
  //   headerName: "Type",
  //   field: "type",
  //   minWidth: 80,
  //   maxWidth: 100,
  //   cellRenderer: (params: any) => {
  //     return (
  //       <div className="h-full flex items-center">
  //         <div className="px-2 py-1 rounded-full text-center text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
  //           B2C
  //         </div>
  //       </div>
  //     );
  //   },
  // },
  {
    headerName: "Gmail",
    field: "gmail",
    minWidth: 200,
    cellRenderer: GmailCell,
  },
  {
    headerName: "Outlook.com",
    field: "outlook",
    minWidth: 200,
    cellRenderer: OutlookCell,
  },
  // {
  //   headerName: "Yahoo! Mail",
  //   field: "yahoo",
  //   minWidth: 200,
  //   cellRenderer: YahooCell,
  // },
  {
    headerName: "AOL Mail",
    field: "aol",
    minWidth: 200,
    cellRenderer: AolCell,
  },
  {
    headerName: "iCloud Mail",
    field: "icloud",
    minWidth: 200,
    cellRenderer: IcloudCell,
  },
  // {
  //   headerName: "GMX",
  //   field: "gmx",
  //   minWidth: 200,
  //   cellRenderer: GmxCell,
  // },
];

// B2B specific columns
export const b2bColumns = [
  // {
  //   headerName: "Type",
  //   field: "type",
  //   minWidth: 80,
  //   maxWidth: 100,
  //   cellRenderer: (params: any) => {
  //     return (
  //       <div className="h-full flex items-center">
  //         <div className="px-2 py-1 rounded-full text-center text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
  //           B2B
  //         </div>
  //       </div>
  //     );
  //   },
  // },

  {
    headerName: "Google Workspace",
    field: "googleworkspace",
    minWidth: 200,
    cellRenderer: GoogleWorkspaceCell,
  },

  {
    headerName: "Microsoft 365",
    field: "microsoft365",
    minWidth: 200,
    cellRenderer: Microsoft365Cell,
  },

  {
    headerName: "Zoho WorkMail",
    field: "zoho",
    minWidth: 200,
    cellRenderer: ZohoCell,
  },
  // {
  //   headerName: "Amazon WorkMail",
  //   field: "amazonWorkMail",
  //   minWidth: 200,
  //   cellRenderer: AmazonWorkMailCell,
  // },
  {
    headerName: "Amazon Abusix",
    field: "amazon abusix",
    minWidth: 200,
    cellRenderer: AmazonAbuseExCell,
  },
  {
    headerName: "Amazon Spamhaus",
    field: "amazonSpamhaus",
    minWidth: 200,
    cellRenderer: AmazonSpamhausCell,
  },
  {
    headerName: "Amazon Trend Micro",
    field: "amazonTrendMicro",
    minWidth: 200,
    cellRenderer: AmazonTrendMicroCell,
  },
  {
    headerName: "Rspamd",
    field: "rspamd",
    minWidth: 200,
    cellRenderer: RspamdCell,
  },
];

// Unified Type column that shows B2B or B2C based on data
// const typeColumn = {
//   headerName: "Type",
//   field: "type",
//   minWidth: 80,
//   maxWidth: 100,
//   cellRenderer: (params: any) => {
//     const type = params.value || params.data?.type || "B2B";
//     const isB2C = type === "B2C" || type === "b2c";
//     return (
//       <div className="h-full flex items-center">
//         <div
//           className={`px-2 py-1 rounded-full text-center text-xs font-semibold ${
//             isB2C
//               ? "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
//               : "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
//           }`}
//         >
//           {isB2C ? "B2C" : "B2B"}
//         </div>
//       </div>
//     );
//   },
// };

// Define unified column definitions that include both B2B and B2C columns
export const b2bColumnDefs = [
  ...baseColumnDefs,
  // typeColumn,
  ...b2bColumns, // Remove duplicate type column
  ...b2cColumns, // Remove duplicate type column and add B2C columns
];

// Keep b2cColumnDefs export for backward compatibility with other files
export const b2cColumnDefs = [...baseColumnDefs, ...b2cColumns];
